import React, { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { userRoles } from '../../data/mockData'
import { useProjects } from '../../hooks/useProjects.jsx'
import { useTranslation } from 'react-i18next'

function FinancialReports() {
  const { t } = useTranslation('money')
  const { user } = useAuth()
  const { projects, loading, getProjectStats } = useProjects()
  const monthLabel = new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })
  const [selectedReport, setSelectedReport] = useState('budget-summary')

  const stats = getProjectStats;

  const financialReport = {
    totalAllocated: stats.totalBudget,
    totalSpent: stats.totalSpent,
    totalRemaining: stats.totalRemaining,
    utilizationRate: stats.totalBudget > 0 ? ((stats.totalSpent / stats.totalBudget) * 100).toFixed(1) + '%' : '0%',
    projectBreakdown: projects.map(p => ({
        project: p.name,
        allocated: p.approved_budget || p.total_budget,
        spent: p.spent_budget || 0,
        remaining: (p.approved_budget || p.total_budget) - (p.spent_budget || 0),
        utilizationRate: (p.approved_budget || p.total_budget) > 0 ? ((p.spent_budget || 0) / (p.approved_budget || p.total_budget) * 100).toFixed(1) + '%' : '0%',
        status: p.status === 'In Progress' ? 'On Track' : p.status
    })),
  }

  // Real risk indicators derived from live project utilization
  const bd = financialReport.projectBreakdown
  const riskHigh = bd.filter(b => parseFloat(b.utilizationRate) >= 80 && parseFloat(b.utilizationRate) < 100).length
  const riskOver = bd.filter(b => parseFloat(b.utilizationRate) >= 100).length
  const riskOnTrack = bd.filter(b => parseFloat(b.utilizationRate) < 80).length

  const reportTypes = [
    { id: 'budget-summary', name: 'Budget Summary', icon: '📊' },
    { id: 'expense-analysis', name: 'Expense Analysis', icon: '💰' },
    { id: 'roi-analysis', name: 'ROI Analysis', icon: '📈' },
    { id: 'variance-report', name: 'Variance Report', icon: '📋' }
  ]

  const canViewFullReports = user?.role === userRoles.ADMIN || 
                           user?.role === userRoles.FINANCE || 
                           user?.role === userRoles.Funder

  return (
    <div className="p-6 space-y-6">
      {/* header */}
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('financial_reports')}</h1>
            <p className="mt-1 opacity-90">{t('comprehensive_analysis')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">{t('current_period')}</p>
            <p className="text-lg font-semibold">{monthLabel}</p>
            <p className="text-sm opacity-90">Utilization: {financialReport?.utilizationRate}</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('report_type')}</label>
                <select
                  value={selectedReport}
                  onChange={(e) => setSelectedReport(e.target.value)}
                  className="form-input w-48"
                >
                  {reportTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.icon} {type.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex space-x-2">
              <button onClick={() => window.print()} className="btn-secondary">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Print / Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('total_allocated')}</p>
              <p className="text-xl font-bold text-gray-900">${financialReport?.totalAllocated?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{t('across_all_projects')}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('total_spent')}</p>
              <p className="text-xl font-bold text-gray-900">${financialReport?.totalSpent?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{t('paid_to_date')}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('remaining')}</p>
              <p className="text-xl font-bold text-gray-900">${financialReport?.totalRemaining?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{t('available_budget')}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('utilization_rate')}</p>
              <p className="text-xl font-bold text-gray-900">{financialReport?.utilizationRate}</p>
              <p className="text-xs text-gray-500">{t('spent_vs_allocated')}</p>
            </div>
          </div>
        </div>
      </div>

      {canViewFullReports ? (
        <>
          {/* Project Breakdown */}
          <div className="card">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('project_budget_breakdown')}</h3>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Allocated
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Spent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Remaining
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {financialReport?.projectBreakdown?.map((project, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{project.project}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">${project.allocated.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">${project.spent.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">${project.remaining.toLocaleString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  parseFloat(project.utilizationRate) > 80 ? 'bg-red-500' :
                                  parseFloat(project.utilizationRate) > 60 ? 'bg-yellow-500' :
                                  'bg-green-500'
                                }`}
                                style={{ width: project.utilizationRate }}
                              ></div>
                            </div>
                            <span className="text-sm text-gray-900">{project.utilizationRate}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            project.status === 'On Track' ? 'bg-green-100 text-green-800' :
                            project.status === 'delayed' ? 'bg-red-100 text-red-800' :
                            project.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {project.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Risk Indicators — derived from live project utilization */}
          <div className="card">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('portfolio_risk')}</h3>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-red-800">{t('high_utilization')}</span>
                <span className="text-lg font-bold text-red-600">{riskHigh}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <span className="text-sm text-yellow-800">{t('budget_overruns')}</span>
                <span className="text-lg font-bold text-yellow-600">{riskOver}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-green-800">{t('on_track_lt80')}</span>
                <span className="text-lg font-bold text-green-600">{riskOnTrack}</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="card">
          <div className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2-2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('access_restricted')}</h3>
            <p className="mt-2 text-gray-600">
              You don't have permission to view detailed financial reports. Contact your administrator for access.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default FinancialReports
